import React, { use, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import CustomAutoComplete from "../../theme/style-components/CustomAutoComplete";
import CustomButton from "../../theme/style-components/CustomButton";
import CustomModal from "../../theme/style-components/CustomModal";
import CustomInput from "../../theme/style-components/CustomInput";
import CustomDatePicker from "../../theme/style-components/CustomDatePicker";
import {
  usePostDepartmentMutation,
  useGetDepartmentsListQuery,
} from "../../store/departments/DepartmentsApiSlice";
import {
  usePostTeamMutation,
  useGetTeamsListQuery,
} from "../../store/teams/TeamsApiSlice";
import { toast } from "react-toastify";
import {
  useAddEmployeeMutation,
  useEditEmployeeMutation,
  useFetchManagersQuery,
  useGetEmployeeQuery,
} from "../../store/employee/EmployeeApiSlice";
import moment from "moment";
import { Box, Grid2, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentUserObject } from "../../store/auth/authSlice";

const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
  title: yup.string().required("Title is required"),
  employment_type: yup.string().required("Employment type is required"),
  departments: yup
    .array()
    .of(yup.string())
    .min(1, "Please select at least one department"),
  phone: yup.string().required("Phone is required"),
  image_url: yup.string().url("Enter a valid URL").nullable(),
  resume_url: yup
    .string()
    .required("Resume url is required")
    .url("Enter a valid URL"),
  linkedin_url: yup
    .string()
    .required("Linkedin url is required")
    .url("Enter a valid URL"),
  job_description_url: yup
    .string()
    .required("Job description url is required")
    .url("Enter a valid URL"),
  job_description: yup
    .string()
    .required("Job Description is required")
    .max(300, "Maximum 300 characters allowed"),
  date_of_joining: yup
    .string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
    .required("Date of Joining is required"),
});

const AddEditEmployee = ({ isEditEmployee, selectedEmployee }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const user = useSelector(selectCurrentUserObject);
  const [addEmployee] = useAddEmployeeMutation();
  const [editEmployee] = useEditEmployeeMutation();
  const { data: DepartmentsData, refetch: refetchDepartmentsData } =
    useGetDepartmentsListQuery({ org_id: user?.company });
  const { data: TeamsData, refetch: refetchTeamsData } = useGetTeamsListQuery(
    {
      org_id: user?.company,
    },
    { skip: !user?.company }
  );

  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [newDepartmentDescription, setNewDepartmentDescription] = useState("");
  const [postDepartment] = usePostDepartmentMutation();
  const [postTeam] = usePostTeamMutation();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const {
    isLoading,
    data: employeeReturned,
    error: isEmployeeError,
    refetch: refetchEmployee,
  } = useGetEmployeeQuery(
    { query: `org_id=${user?.company}` },
    { skip: !user?.company }
  );

  // Add this new query for managers
  const {
    data: managersReturned,
    isLoading: isLoadingManagers,
    error: managersError,
  } = useFetchManagersQuery({ query: user?.company });

  const handleCancelImage = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    formik.setFieldValue("image", "");
    formik.setFieldValue("image_url", "");
    // Reset file input
    const fileInput = document.getElementById("image-upload");
    if (fileInput) fileInput.value = "";
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    // Check if image_url is already set
    if (formik.values.image_url && formik.values.image_url.trim() !== "") {
      toast.error("Please use either image upload or image URL, not both");
      event.target.value = ""; // Reset the input
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPEG, PNG, GIF, or WEBP)");
      event.target.value = "";
      return;
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error("File size should be less than 5MB");
      event.target.value = ""; // Reset the input
      return;
    }

    setSelectedFile(file);

    // Create preview URL for display only
    const imageUrl = URL.createObjectURL(file);
    setPreviewUrl(imageUrl);
    formik.setFieldValue("image_url", ""); // Clear the image_url text input if a file is selected
  };

  // Cleanup preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleImageUrlChange = (e) => {
    const url = e.target.value;

    // Check if a file is already selected
    if (selectedFile) {
      toast.error("Please use either image upload or image URL, not both");
      e.target.value = ""; // Reset the input
      return;
    }

    formik.handleChange(e); // Update formik value
    if (url && url.trim() !== "") {
      setPreviewUrl(url);
      setSelectedFile(null); // Clear selected file when URL is provided
      // Reset file input
      const fileInput = document.getElementById("image-upload");
      if (fileInput) fileInput.value = "";
      // Set image to null when URL is provided
      formik.setFieldValue("image", null);
      // Force update the form values
      formik.setValues({
        ...formik.values,
        image: null,
        image_url: url,
      });
    } else {
      setPreviewUrl("");
      formik.setFieldValue("image", "");
      formik.setFieldValue("image_url", "");
    }
  };

  const formik = useFormik({
    validationSchema,
    initialValues: {
      name: "",
      departments: [],
      teams: [],
      managers: [],
      title: "",
      office_location: "",
      employment_type: "",
      date_of_joining: "",
      email: "",
      phone: "",
      image: "",
      image_url: "",
      resume_url: "",
      linkedin_url: "",
      job_description_url: "",
      job_description: "",
      certifications: [],
    },
    enableReinitialize: true, // Ensures form updates when selectedEmployee changes
    onSubmit: async (values) => {
      try {
        let employeeData = { ...values };

        // Validate that we don't have both image and image_url
        if (
          selectedFile &&
          values.image_url &&
          values.image_url.trim() !== ""
        ) {
          toast.error("Please use either image upload or image URL, not both");
          return;
        }

        // Handle image data
        if (values.image_url && values.image_url.trim() !== "") {
          // If URL exists, use that as profile image
          employeeData = {
            ...employeeData,
            image_url: values.image_url,
            image: "", // Set image to empty string when URL is provided
          };
        } else if (selectedFile) {
          // Only use file if no URL is provided
          employeeData = {
            ...employeeData,
            image: selectedFile,
            image_url: "", // Clear URL if file exists
          };
        } else {
          employeeData = {
            ...employeeData,
            image: "",
            image_url: "", // Clear URL if no file exists
          };
        }

        if (isEditEmployee && selectedEmployee) {
          const updatedEmployee = {
            ...employeeData,
            employee_id: selectedEmployee?.id,
          };

          await editEmployee(updatedEmployee).unwrap();
          refetchEmployee();
          toast.success("Employee updated successfully");
        } else {
          const newEmployee = {
            ...employeeData,
            org_id: user?.company,
          };
          await addEmployee(newEmployee).unwrap();
          toast.success("Employee added successfully");
        }
        navigate("/employee");
      } catch (error) {
        toast.error("Something went wrong!!");
      }
    },
  });

  useEffect(() => {
    if (isEditEmployee && selectedEmployee) {
      // Handle image data first
      let imageData = {
        image: null,
        image_url: "",
      };

      if (selectedEmployee?.image_url) {
        imageData = {
          image: null,
          image_url: selectedEmployee.image_url,
        };
        setPreviewUrl(selectedEmployee.image_url);
        setSelectedFile(null); // Reset selected file when URL is present
      } else if (selectedEmployee?.image) {
        const baseUrl =
          process.env.REACT_APP_MEDIA_URL || "http://localhost:8000";
        const fullImageUrl = `${baseUrl}${selectedEmployee.image}`;
        setPreviewUrl(fullImageUrl);
        setSelectedFile(null); // Reset selected file
      } else {
        setPreviewUrl("");
        setSelectedFile(null);
      }

      formik.setValues({
        name: selectedEmployee?.name || "",
        departments: selectedEmployee?.departments || [],
        teams: selectedEmployee?.teams || [],
        managers: selectedEmployee?.managers || [],
        title: selectedEmployee?.title || "",
        office_location: selectedEmployee?.office_location || "",
        employment_type: selectedEmployee?.employment_type || "",
        date_of_joining: selectedEmployee?.date_of_joining
          ? moment(selectedEmployee.date_of_joining).format("YYYY-MM-DD")
          : "",
        email: selectedEmployee?.email || "",
        phone: selectedEmployee?.phone || "",
        ...imageData, // Spread the image data
        resume_url: selectedEmployee?.resume_url || "",
        linkedin_url: selectedEmployee?.linkedin_url || "",
        job_description_url: selectedEmployee?.job_description_url || "",
        job_description: selectedEmployee?.job_description || "",
        certifications: selectedEmployee?.certifications || [],
      });

      setSelectedFile(null); // Reset selected file
      setSelectedDepartments(
        DepartmentsData?.data?.filter((dep) =>
          selectedEmployee.departments.includes(dep.id)
        ) || []
      );
      setSelectedTeams(
        TeamsData?.data?.filter((team) =>
          selectedEmployee.teams.includes(team.id)
        ) || []
      );
      setSelectedManagers(
        employeeReturned?.data?.filter((manager) =>
          selectedEmployee.managers.includes(manager.id)
        ) || []
      );

      setSelectedDate(
        selectedEmployee.date_of_joining
          ? moment(selectedEmployee.date_of_joining).toDate()
          : null
      );
    }
  }, [
    isEditEmployee,
    selectedEmployee,
    DepartmentsData,
    TeamsData,
    employeeReturned,
  ]);

  const handleOpenModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleAddEntry = async (event) => {
    event.preventDefault();

    if (!newDepartmentName.trim() || !newDepartmentDescription.trim()) {
      alert("Please enter both name and description.");
      return;
    }

    const newEntry = {
      org_id: user?.company,
      name: newDepartmentName,
      description: newDepartmentDescription,
    };

    if (modalType === "department") {
      console.log("Adding to departments...");
      try {
        const result = await postDepartment(newEntry)
          .unwrap()
          .then((res) => {
            console.log(res, "res");
            toast.success("Record added successfully");
            // Reset and close modal
            refetchDepartmentsData();
            setNewDepartmentName("");
            setNewDepartmentDescription("");
            setIsModalOpen(false);
          });
      } catch (error) {
        toast.error(`Something went wrong!!`);
      }
    } else {
      console.log("Adding to teams...");
      try {
        const result = await postTeam(newEntry)
          .unwrap()
          .then((res) => {
            console.log(res, "res");
            toast.success("Record added successfully");
            // Reset and close modal
            refetchTeamsData();
            setNewDepartmentName("");
            setNewDepartmentDescription("");
            setIsModalOpen(false);
          });
      } catch (error) {
        toast.error(`Something went wrong!!`);
      }
    }
  };

  return (
    <>
      <form>
        <Grid2 container spacing={2} sx={{ mb: 3 }}>
          <Grid2 xs={12}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px", mb: 2 }}>
                Profile Image
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {previewUrl && (
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="Profile preview"
                    sx={{
                      width: 100,
                      height: 100,
                      objectFit: "cover",
                      borderRadius: "50%",
                      border: "1px solid #ccc",
                    }}
                  />
                )}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <input
                    type="file"
                    id="image-upload"
                    style={{ display: "none" }}
                    accept="image/*"
                    onChange={handleFileSelect}
                  />
                  <CustomButton
                    color="primary"
                    type="button"
                    buttonName={selectedFile ? "Change Image" : "Upload Image"}
                    onClick={() =>
                      document.getElementById("image-upload").click()
                    }
                  />
                  {(selectedFile || (isEditEmployee && previewUrl)) && (
                    <CustomButton
                      color="error"
                      type="button"
                      buttonName="Cancel"
                      onClick={handleCancelImage}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Grid2>
        </Grid2>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Name
              </Typography>
              <CustomInput
                id="name"
                name="name"
                placeholder={"Enter name"}
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                sx={{ maxWidth: 50 }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Title
              </Typography>
              <CustomInput
                id="title"
                name="title"
                placeholder={"Enter title"}
                value={formik.values.title}
                onChange={formik.handleChange}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
                sx={{ maxWidth: 50 }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Select Departments
              </Typography>
              <CustomAutoComplete
                multiple
                options={
                  DepartmentsData?.data
                    ? [...DepartmentsData.data, { id: "add", name: "+ Add" }]
                    : [{ id: "add", name: "+ Add" }]
                }
                getOptionLabel={(option) => option.name}
                value={selectedDepartments}
                onChange={(event, value) => {
                  if (value.some((v) => v.id === "add")) {
                    handleOpenModal("department");
                  } else {
                    setSelectedDepartments(value);
                    formik.setFieldValue(
                      "departments",
                      value.map((dep) => dep.id)
                    );
                  }
                }}
                // label="Select Departments"
                placeholder="Departments"
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Select Teams
              </Typography>
              <CustomAutoComplete
                multiple
                options={
                  TeamsData?.data
                    ? [...TeamsData.data, { id: "add", name: "+ Add" }]
                    : [{ id: "add", name: "+ Add" }]
                }
                getOptionLabel={(option) => option.name}
                value={selectedTeams}
                onChange={(event, value) => {
                  if (value.some((v) => v.id === "add")) {
                    handleOpenModal("team");
                  } else {
                    setSelectedTeams(value);
                    formik.setFieldValue(
                      "teams",
                      value.map((team) => team.id)
                    );
                  }
                }}
                placeholder="Teams"
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Select Managers
              </Typography>
              <CustomAutoComplete
                multiple
                // options={
                //   employeeReturned?.data ? [...employeeReturned.data] : []
                // }
                options={managersReturned?.length > 0 ? managersReturned : []}
                getOptionLabel={(option) => option.name}
                value={selectedManagers}
                onChange={(event, value) => {
                  setSelectedManagers(value);
                  formik.setFieldValue(
                    "managers",
                    value.map((manager) => manager.id)
                  );
                }}
                placeholder="Managers"
              />
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Office location
              </Typography>
              <CustomInput
                id="office_location"
                name="office_location"
                placeholder={"Enter office location"}
                value={formik.values.office_location}
                onChange={formik.handleChange}
                error={
                  formik.touched.office_location &&
                  Boolean(formik.errors.office_location)
                }
                helperText={
                  formik.touched.office_location &&
                  formik.errors.office_location
                }
                sx={{ maxWidth: 50 }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Employment type
              </Typography>
              <CustomInput
                id="employment_type"
                name="employment_type"
                placeholder={"Enter employment type"}
                value={formik.values.employment_type}
                onChange={formik.handleChange}
                error={
                  formik.touched.employment_type &&
                  Boolean(formik.errors.employment_type)
                }
                helperText={
                  formik.touched.employment_type &&
                  formik.errors.employment_type
                }
                sx={{ maxWidth: 50 }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Joining Date
              </Typography>
              <CustomDatePicker
                selectedDate={selectedDate}
                placeholderText="Select date"
                error={
                  formik.touched.date_of_joining &&
                  Boolean(formik.errors.date_of_joining)
                }
                helperText={
                  formik.touched.date_of_joining &&
                  formik.errors.date_of_joining
                }
                sx={{ maxWidth: 50 }}
                onChange={(date) => {
                  setSelectedDate(date);
                  formik.setFieldValue(
                    "date_of_joining",
                    moment(date).format("YYYY-MM-DD")
                  );
                }}
                // minDate={new Date()}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Email
              </Typography>
              <CustomInput
                id="email"
                name="email"
                placeholder={"Enter email"}
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                sx={{ maxWidth: 50 }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Phone number
              </Typography>
              <CustomInput
                id="phone"
                name="phone"
                placeholder={"Enter phone number"}
                value={formik.values.phone}
                onChange={formik.handleChange}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                sx={{ maxWidth: 50 }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Image url
              </Typography>
              <CustomInput
                id="image_url"
                name="image_url"
                placeholder={"Enter image url"}
                value={formik.values.image_url}
                onChange={handleImageUrlChange}
                error={
                  formik.touched.image_url && Boolean(formik.errors.image_url)
                }
                helperText={formik.touched.image_url && formik.errors.image_url}
                sx={{ maxWidth: 50 }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Resume url
              </Typography>
              <CustomInput
                id="resume_url"
                name="resume_url"
                placeholder={"Enter resume url"}
                value={formik.values.resume_url}
                onChange={formik.handleChange}
                error={
                  formik.touched.resume_url && Boolean(formik.errors.resume_url)
                }
                helperText={
                  formik.touched.resume_url && formik.errors.resume_url
                }
                sx={{ maxWidth: 50 }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Linkedin url
              </Typography>
              <CustomInput
                id="linkedin_url"
                name="linkedin_url"
                placeholder={"Enter linkedin url"}
                value={formik.values.linkedin_url}
                onChange={formik.handleChange}
                error={
                  formik.touched.linkedin_url &&
                  Boolean(formik.errors.linkedin_url)
                }
                helperText={
                  formik.touched.linkedin_url && formik.errors.linkedin_url
                }
                sx={{ maxWidth: 50 }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Job description
              </Typography>
              <CustomInput
                id="job_description"
                name="job_description"
                placeholder={"Enter job description"}
                value={formik.values.job_description}
                onChange={formik.handleChange}
                error={
                  formik.touched.job_description &&
                  Boolean(formik.errors.job_description)
                }
                helperText={
                  formik.touched.job_description &&
                  formik.errors.job_description
                }
                sx={{ maxWidth: 50 }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: "15px" }}>
                Job description url
              </Typography>
              <CustomInput
                id="job_description_url"
                name="job_description_url"
                placeholder={"Enter job description url"}
                value={formik.values.job_description_url}
                onChange={formik.handleChange}
                error={
                  formik.touched.job_description_url &&
                  Boolean(formik.errors.job_description_url)
                }
                helperText={
                  formik.touched.job_description_url &&
                  formik.errors.job_description_url
                }
                sx={{ maxWidth: 50 }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <Box display={"flex"} justifyContent={"end"} gap={1.5}>
              <CustomButton
                color="primary"
                type="button"
                buttonName="Cancel"
                onClick={() => navigate("/employee")}
              />
              <CustomButton
                color="primary"
                type="button"
                buttonName="Submit"
                onClick={() => formik.handleSubmit()}
              />
            </Box>
          </Grid2>
        </Grid2>
      </form>

      <CustomModal
        isModalOpen={isModalOpen}
        isShowActionButton={false}
        handleClose={() => setIsModalOpen(false)}
        title={`Add ${modalType === "department" ? "Department" : "Team"}`}
      >
        <input
          type="text"
          name="name"
          placeholder={`Enter ${
            modalType === "department" ? "department" : "team"
          } name`}
          value={newDepartmentName}
          onChange={(e) => setNewDepartmentName(e.target.value)}
          style={{ width: "100%", padding: "10px", marginBottom: "10px" }}
        />
        <input
          type="text"
          name="description"
          placeholder={`Enter ${
            modalType === "department" ? "department" : "team"
          } description`}
          value={newDepartmentDescription}
          onChange={(e) => setNewDepartmentDescription(e.target.value)}
          style={{ width: "100%", padding: "10px", marginBottom: "10px" }}
        />

        <CustomButton
          type="button"
          color="primary"
          buttonName="Add"
          onClick={handleAddEntry}
        />
      </CustomModal>
    </>
  );
};

export default AddEditEmployee;
